import { config, services } from "@/config";
import {
  appendHistory,
  cancelReserva,
  createReserva,
  findService,
  historyEntry,
  takenTimes,
} from "@/services/booking";
import { uid } from "@/services/format";
import { store } from "@/services/storage";
import { openWhatsApp, reservationMessage } from "@/services/whatsapp";
import { resetBooking, state } from "@/state";
import { byId } from "./dom";
import { go, isViewId } from "./navigation";
import {
  renderBookServices,
  renderCalendar,
  renderContactos,
  renderReservas,
  renderServices,
  renderSlots,
  showStep,
} from "./rendering";
import { toast } from "./toast";

/* ============================================================
   Acciones del usuario (controlador de la SPA)
   ============================================================ */

function navigate(viewId: string): void {
  if (isViewId(viewId)) go(viewId);
}

function preselectService(id: string): void {
  state.booking.service = id;
  go("reservar");
  renderBookServices();
  showStep(1);
  byId<HTMLButtonElement>("toStep2").disabled = false;
  toast("Servicio seleccionado. Continúa al paso 2.");
}

function pickService(id: string): void {
  state.booking.service = id;
  renderBookServices();
  byId<HTMLButtonElement>("toStep2").disabled = false;
}

function selectCategory(cat: string): void {
  state.activeCat = cat;
  renderServices();
}

function selectDate(iso: string): void {
  state.booking.date = iso;
  renderCalendar();
  byId<HTMLButtonElement>("toStep3").disabled = false;
}

function selectTime(time: string): void {
  const taken = takenTimes(store.getReservas(), state.booking.date ?? "");
  if (taken.includes(time)) {
    toast("Hora ya reservada, elige otra.");
    return;
  }
  state.booking.time = time;
  renderSlots();
  byId<HTMLButtonElement>("toStep4").disabled = false;
}

function goToStep(step: number): void {
  if (step === 1) {
    showStep(1);
    return;
  }
  if (step === 2) {
    if (!state.booking.date) {
      toast("Elige una fecha.");
      return;
    }
    showStep(2);
    return;
  }
  if (step === 3) {
    if (!state.booking.time) {
      toast("Elige una hora.");
      return;
    }
    showStep(3);
  }
}

function nextFromStep1(): void {
  if (!state.booking.service) {
    toast("Elige un servicio.");
    return;
  }
  showStep(2);
}

function nextFromStep2(): void {
  if (!state.booking.date) {
    toast("Elige una fecha.");
    return;
  }
  showStep(3);
}

function nextFromStep3(): void {
  if (!state.booking.time) {
    toast("Elige una hora.");
    return;
  }
  showStep(4);
}

function confirmReservation(): void {
  const name = byId<HTMLInputElement>("fName").value.trim();
  const phone = byId<HTMLInputElement>("fPhone").value.trim();
  const note = byId<HTMLInputElement>("fNote").value.trim();

  if (!name || !phone) {
    toast("Completa nombre y teléfono.");
    return;
  }

  const service = findService(services, state.booking.service);
  if (!service || !state.booking.date || !state.booking.time) {
    toast("Completa servicio, fecha y hora.");
    return;
  }

  state.booking.name = name;
  state.booking.phone = phone;
  state.booking.note = note;

  const reserva = createReserva(
    service,
    state.booking.date,
    state.booking.time,
    name,
    phone,
    note,
  );

  const reservas = store.getReservas();
  reservas.push(reserva);
  store.setReservas(reservas);
  store.setContactos(
    appendHistory(store.getContactos(), phone, historyEntry(reserva)),
  );

  openWhatsApp(config.phoneWA, reservationMessage(config, service, state.booking));
  toast("Reserva guardada. ¡Confirma en WhatsApp!");

  resetBooking();
  byId<HTMLInputElement>("fName").value = "";
  byId<HTMLInputElement>("fPhone").value = "";
  byId<HTMLInputElement>("fNote").value = "";
  byId<HTMLButtonElement>("toStep2").disabled = true;
  byId<HTMLButtonElement>("toStep3").disabled = true;
  byId<HTMLButtonElement>("toStep4").disabled = true;

  window.setTimeout(() => go("agenda"), 1200);
}

function cancel(id: string): void {
  store.setReservas(cancelReserva(store.getReservas(), id));
  toast("Reserva cancelada. Reagenda por WhatsApp.");
  renderReservas();
}

function toggleAdmin(): void {
  const panel = byId("adminPanel");
  const hidden = panel.style.display === "none" || panel.style.display === "";
  panel.style.display = hidden ? "block" : "none";
  byId("adminToggle").setAttribute("aria-expanded", String(hidden));
  if (hidden) renderReservas();
}

function showContactForm(show: boolean): void {
  const form = byId("contactForm");
  form.style.display = show ? "block" : "none";
  byId("addContactBtn").setAttribute("aria-expanded", String(show));
  if (show) byId<HTMLInputElement>("cName").focus();
}

function saveContact(): void {
  const name = byId<HTMLInputElement>("cName").value.trim();
  const phone = byId<HTMLInputElement>("cPhone").value.trim();
  const notes = byId<HTMLInputElement>("cNotes").value.trim();

  if (!name) {
    toast("Ingresa el nombre.");
    return;
  }

  const contactos = store.getContactos();
  contactos.push({
    id: uid(),
    name,
    phone: phone || "—",
    notes,
    history: [],
  });
  store.setContactos(contactos);

  byId<HTMLInputElement>("cName").value = "";
  byId<HTMLInputElement>("cPhone").value = "";
  byId<HTMLInputElement>("cNotes").value = "";

  showContactForm(false);
  toast("Cliente guardado en el historial.");
  renderContactos();
}

function deleteContact(id: string): void {
  store.setContactos(store.getContactos().filter((c) => c.id !== id));
  toast("Cliente eliminado.");
  renderContactos();
}

/* ============================================================
   Delegación de eventos (un solo listener para contenido dinámico)
   ============================================================ */

function closest(target: EventTarget | null, selector: string): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>(selector);
}

function handleClick(event: MouseEvent): void {
  const target = event.target;

  const nav = closest(target, "[data-nav]");
  if (nav?.dataset.nav) {
    event.preventDefault();
    navigate(nav.dataset.nav);
    return;
  }

  const book = closest(target, "[data-book]");
  if (book?.dataset.book) {
    preselectService(book.dataset.book);
    return;
  }

  const pick = closest(target, "[data-pickservice]");
  if (pick?.dataset.pickservice) {
    pickService(pick.dataset.pickservice);
    return;
  }

  const cat = closest(target, "[data-cat]");
  if (cat?.dataset.cat) {
    selectCategory(cat.dataset.cat);
    return;
  }

  const date = closest(target, "[data-date]");
  if (date?.dataset.date) {
    if (date.getAttribute("aria-disabled") === "true") {
      toast("Los domingos estamos cerrados.");
      return;
    }
    selectDate(date.dataset.date);
    return;
  }

  const time = closest(target, "[data-time]");
  if (time?.dataset.time) {
    selectTime(time.dataset.time);
    return;
  }

  const goto = closest(target, "[data-goto]");
  if (goto?.dataset.goto) {
    goToStep(Number(goto.dataset.goto));
    return;
  }

  const cancelBtn = closest(target, "[data-cancel]");
  if (cancelBtn?.dataset.cancel) {
    cancel(cancelBtn.dataset.cancel);
    return;
  }

  const deleteBtn = closest(target, "[data-delcontact]");
  if (deleteBtn?.dataset.delcontact) {
    deleteContact(deleteBtn.dataset.delcontact);
  }
}

/** Conecta todos los eventos de la app (estáticos y delegados). */
export function initEvents(): void {
  document.addEventListener("click", handleClick);

  byId("toStep2").addEventListener("click", nextFromStep1);
  byId("toStep3").addEventListener("click", nextFromStep2);
  byId("toStep4").addEventListener("click", nextFromStep3);
  byId("confirmBtn").addEventListener("click", confirmReservation);

  byId("adminToggle").addEventListener("click", toggleAdmin);

  byId("addContactBtn").addEventListener("click", () => showContactForm(true));
  byId("cancelContactBtn").addEventListener("click", () => showContactForm(false));
  byId("saveContactBtn").addEventListener("click", saveContact);
  byId("contactSearch").addEventListener("input", renderContactos);
}
